import { Dialog, DialogActions, DialogContent, Grid, Typography } from "@mui/material";
import { AppImageDimension, AppImages } from "app/appImages";
import { MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator, MprcTextMuted } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { MprcImageSelection } from "components/MprcImageHandler";
import { FileGetDto } from "generated/CoreApi_v1";
import MediaUtils from "lib/MediaUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicationUploadingStatus } from "views/knowledge/publication/creation";

////////////////////////////////////////////////////////////////////////////////

interface IState {
    thumbnail: FileGetDto | null;
    uploadingStatus: PublicationUploadingStatus;
}

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    publicationId: string;
    title?: string;
    onClose: (changed: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const PublicationThumbnailDialog = (props: IPros) => {
    const { publicationId, title, onClose } = props;

    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");

    const [state, setState] = useState<IState>({
        thumbnail: null,
        uploadingStatus: "none",
    });

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [canSave, setCanSave] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();

    ///
    const handleImageChange = (data: FileGetDto | null) => {
        setCanSave(true);
        setState({
            ...state,
            thumbnail: data
        });
    };

    ///
    const handleStartUpload = () => {
        setState({
            ...state,
            uploadingStatus: "uploading"
        });
    };


    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.knowledgePublication.getThumbnailPublication({ publicationId });

                setState({
                    ...state,
                    thumbnail: result.data.thumbnail
                });
            }
            catch (e) {
                setViewError(e as Error);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);


    // Ao salvar
    useEffect(() => {
        async function postData() {
            try {
                var result = await api.v1.knowledgePublication.updateThumbnailPublication({
                    publicationId,
                    thumbnailId: state.thumbnail?.fileId ?? null
                });

                if (result) {
                    enqueueSnackbar(t("Feed.Card.Actions.PublicationThumbnailDialog.SuccessMessage"), { variant: "success" });
                    onClose(true);
                }
            }
            catch (e) {
                setViewError(e as Error);
            }
            finally {
                setSaving(false);
            }
        }

        if (saving) postData();

    }, [saving]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />


            <Dialog
                open
                fullWidth
                maxWidth="sm"
                onClose={() => onClose(false)}
            >

                <MprcDialogTitleClose
                    title={t("Feed.Card.Actions.PublicationThumbnailDialog.Title")}
                    subtitle={title}
                    shrinkPadding
                    onClose={() => onClose(false)}
                />

                <DialogContent>

                    <MprcRequestIndicator visible={loading} type={"request"} />

                    <Grid container spacing={2}>
                        <Grid>
                            <MprcImageSelection
                                allowEdit
                                enableClear
                                initialSource="local"
                                backgroundType="solid"
                                selectButtomText={t("Feed.Card.Actions.PublicationThumbnailDialog.SelectImageButtomText")}
                                emptyImageUrl={AppImages.KnowledgeListThumbnail}
                                currentImageUrl={state.thumbnail?.url}
                                acceptedFiles={MediaUtils.DropzoneAcceptImages()}
                                imageDimensions={AppImageDimension.ThumbnailWide}
                                onClear={() => { handleImageChange(null); }}
                                onFileUploadSuccess={(data) => {
                                    handleImageChange(data.thumbnail ? data.thumbnail : data.mainFile);
                                    return true;
                                }}
                                onFileUploadStarted={() => handleStartUpload()}
                            />
                        </Grid>
                        <Grid size="grow">
                            <Grid container direction="column" spacing={2}>
                                <Grid>
                                    <MprcTextMuted>
                                        <Typography variant="caption">
                                            {t("Feed.Card.Actions.PublicationThumbnailDialog.Tooltip_L1")}
                                        </Typography>
                                    </MprcTextMuted>
                                </Grid>
                                <Grid>
                                    <MprcTextMuted>
                                        <Typography variant="caption">
                                            {t("Feed.Card.Actions.PublicationThumbnailDialog.Tooltip_L2")}
                                        </Typography>
                                    </MprcTextMuted>
                                </Grid>
                                <Grid>
                                    <Typography variant="bodyNotice">
                                        {t("Feed.Card.Actions.PublicationThumbnailDialog.Tooltip_L3")}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <ButtonCancel
                        disabled={
                            loading
                            || state.uploadingStatus == "uploading"
                        }
                        variant="contained"
                        onClick={() => onClose(false)}
                    />
                    <ButtonSave
                        disabled={
                            loading
                            || state.uploadingStatus == "uploading"
                            || !canSave
                        }
                        variant="contained"
                        onClick={() => setSaving(true)}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
